import type { IconDefinition } from '@fortawesome/fontawesome-common-types';

export type APIList<T> = {
  object?: 'list';
  total_count: number;
  data: T[];
  next?: string;
  previous?: string;
};

export type CreateNewConversationPayload = {
  agent_name: string;
  context_metadata: AgentContext;
  name: string;
};

export type CreateNewConversationResponse = {
  object: 'conversation';
  id: string;
  agent_name: string;
  context_metadata: AgentContext;
  name: string;
  openai_conversation_id: string;
  created_at: string;
  updated_at: string;
  messages: Array<never>;
};

export type PostMessagePayload = {
  input_message: string;
  context: AgentContext;
};

export type PostMessageResponse = {
  object: 'assistant_message';
  id: string; // UUID in hex format
  thread_id: string; // UUID in hex format
  input_message: string;
  response_message: string;
  status: string;
  response: string;
  feedback?: number; // 1 or -1
  actions: AgentAction[];
  app_actions: AgentAppAction[];
  created_at: string; // ISO 8601 datetime
  updated_at: string; // ISO 8601 datetime
};

export type GetConversationsQueryParams = {
  offset?: number;
  limit?: number;
};

export type GetConversationsResponse = APIList<ConversationListItem>;

export type ConversationListItem = {
  object: 'conversation';
  id: string;
  agent_name: string;
  name: string;
  created_at: string; // ISO 8601 datetime
  updated_at: string; // ISO 8601 datetime
};

export type GetSingleConversationResponse = {
  object: 'conversation';
  id: string;
  agent_name: string;
  context_metadata: Record<string, string | number>;
  name: string;
  openai_conversation_id: string;
  created_at: string;
  updated_at: string;
  messages: Array<AgentMessage>;
};

export type AgentMessage = {
  object: 'assistant_message';
  id: string; // UUID in hex format
  thread_id?: string; // UUID in hex format
  input_message: string;
  response_message: string;
  status: string;
  response: string;
  feedback?: number; // 1 or -1
  actions: AgentAction[];
  app_actions: AgentAppAction[];
  created_at: string; // ISO 8601 datetime
  updated_at: string; // ISO 8601 datetime
};

// Alias for backwards compatibility
export type AssistantMessage = AgentMessage;

type AgentAction = {
  object: 'assistant_action';
  id: string; // UUID in hex format
  message_id: string; // UUID
  tool_name: string;
  status: string;
  order: number;
  response: string;
  actioned_at: string; // ISO 8601 datetime
  created_at: string; // ISO 8601 datetime
  updated_at: string; // ISO 8601 datetime
};

export type AgentAppAction = {
  name: string;
  action_data: Record<string, any>;
};

export type AgentContext = Record<string, string | number | undefined>;

/**
 * Agent name identifiers matching backend kit_name values.
 * These are used for consistent agent identification across frontend and backend.
 */
export enum AgentNames {
  PLAN_DOCUMENT_AGENT = 'planexecution.assistant.plan_document_agent',
  PAYOUTS_INFO_AGENT = 'payouts.assistant.payouts_info_agent',
  PLAN_COMPREHENSION_AGENT = 'worksheets.modeling.plan_comprehension',
  WHAT_IF_AGENT = 'payee.assistant.what_if_agent',
  /** @deprecated Use PLAN_COMPREHENSION_AGENT instead */
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  FORMULAS_AGENT = 'worksheets.modeling.plan_comprehension',
  CATALYST_ML_TRAINING_AGENT = 'catalyst.ml.training_run_explainer',
  CATALYST_ML_PREDICTION_AGENT = 'catalyst.ml.prediction_explainer',
  INQUIRIES_AGENT = 'disputes.inquiries',
  PAYEE_AGENT = 'payee.assistant.payee_agent',
  TRANSFORMATION_AGENT = 'worksheets.kits.transformation',
  ACCOUNT_DEALER_AGENT = 'tq.account_dealer',
  CATALYST_EPM_AGENT = 'catalyst.epm.agent',
  AUDIT_LOGS_AGENT = 'audit.assistkit.audit_logs_agent',
}

export type Message = {
  id: string;
  type: 'user' | 'agent';
  contents: string;
};

export type MessageButtonSpec = {
  id: string;
  icon: IconDefinition;
  onClick: () => void;
  tooltip?: string;
  disabled?: boolean;
  /** When provided, renders a labeled button instead of an icon-only button. */
  label?: string;
  /** Button variant when rendering as a labeled button. Defaults to 'outlined'. */
  variant?: 'contained' | 'outlined' | 'text';
};

export type AppActionButtonPosition = 'top' | 'bottom' | 'left' | 'right';

export type AppActionConfig = {
  name: string;
  buttons?: MessageButtonSpec[];
  buttonPosition?: AppActionButtonPosition;
};

export type AppActionContext = {
  userMessageId: string;
  agentMessageId: string;
};

export type AsyncMessageSubmitResponse = { status: 'pending'; request_id: string };
export type AsyncMessageStatusResponse =
  | { status: 'pending' }
  | { status: 'completed'; data: PostMessageResponse }
  | { status: 'error'; error: string };
